import React, { useState, useEffect, useRef } from 'react';
import { Trip, Day } from '../types';
import { TripPlannerAPI } from '../services/api';

interface Recommendation {
  id: string;
  name: string;
  placeName: string;
  description: string;
  type: string;
  location: {
    name: string;
    lat: number;
    lon: number;
  };
  estimatedCost: number;
  isLiked: boolean;
  isDisliked: boolean;
}

// Module-level cache to persist across component remounts (shared with RecommendationsModal pattern)
const recommendationsCache = new Map<string, Recommendation[]>();
const loadingCache = new Set<string>();
const lastApiCallTime = new Map<string, number>(); // Track when we last called API for each key
const MIN_API_CALL_INTERVAL = 5000; // Minimum 5 seconds between API calls for same key

const getCacheKey = (tripId: string, dayId: string) => `${tripId}:${dayId}`;

const clearCacheForKey = (key: string) => {
  recommendationsCache.delete(key);
  loadingCache.delete(key);
  lastApiCallTime.delete(key);
};

interface AddItineraryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (itemData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: {
      lat: number;
      lon: number;
    };
    itemType: string;
    estimatedCost: number;
  }) => void;
  onAIGenerate: (prompt: string) => Promise<any>;
  onAIGeneratePreview?: (prompt: string) => Promise<any>;
  onAIEdit?: (editDescription: string) => Promise<any>;
  dayIndex: number;
  day: Day;
  trip: Trip;
  editingItem?: any;
  isSaving?: boolean;
  isDeleting?: boolean;
  onAddActivity?: (dayIndex: number, item: any) => void;
  // Form state managed in parent (like CreateTripModal)
  formData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
    aiEditPrompt: string;
    mode: 'recommendations' | 'ai' | 'manual';
    isAIMode: boolean;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
    aiEditPrompt: string;
    mode: 'recommendations' | 'ai' | 'manual';
    isAIMode: boolean;
  }>>;
}


const AddItineraryItemModal: React.FC<AddItineraryItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onAIGenerate: _onAIGenerate, // Reserved for future AI mode implementation
  onAIGeneratePreview,
  onAIEdit,
  dayIndex,
  day,
  trip,
  editingItem,
  isSaving = false,
  isDeleting = false,
  onAddActivity,
  formData,
  setFormData
}) => {
  // Use formData from props (managed in parent) - this persists across remounts
  const name = formData.name;
  const placeName = formData.placeName;
  const description = formData.description;
  const startTime = formData.startTime;
  const endTime = formData.endTime;
  const location = formData.location;
  const itemType = formData.itemType;
  const estimatedCost = formData.estimatedCost;
  const aiEditPrompt = formData.aiEditPrompt;
  const mode = formData.mode;
  const isAIMode = formData.isAIMode;

  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const scrollPositionRef = useRef<number>(0);

  // Recommendations state
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [addedRecommendations, setAddedRecommendations] = useState<Set<string>>(new Set());
  const [addingRecommendationId, setAddingRecommendationId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollIndex, setScrollIndex] = useState(0);
  const scrollRafRef = useRef<number | null>(null);
  const hasInitializedRef = useRef(false);

  const isEditing = !!editingItem;

  // Load recommendations when mode is 'recommendations' and modal opens
  // Uses the same caching mechanism as RecommendationsModal to prevent duplicate API calls
  useEffect(() => {
    // Only execute when modal transitions from closed to open
    if (isOpen && !isEditing && mode === 'recommendations') {
      const cacheKey = getCacheKey(trip.tripId, day.dayId);
      
      // PRIMARY GUARD: Check module-level cache FIRST (persists across component remounts)
      // This is the most important check - if data exists in cache, use it and skip everything
      const cachedData = recommendationsCache.get(cacheKey);
      if (cachedData) {
        console.log('🔄 Loading recommendations from cache for', cacheKey, '- skipping API call');
        setRecommendations(cachedData);
        setAddedRecommendations(new Set());
        setCurrentIndex(0);
        hasInitializedRef.current = true;
        return; // Exit early - no API call needed
      }
      
      // SECONDARY GUARD: Check if already loading this specific day/trip
      if (loadingCache.has(cacheKey)) {
        console.log('⚠️ Already loading this day/trip, restoring loading state...');
        hasInitializedRef.current = true;
        // Restore loading state since API call is still in progress
        setLoadingRecommendations(true);
        setRecommendations([]); // Clear any stale data
        return; // Exit early - already loading
      }
      
      // TERTIARY GUARD: Rate limiting - prevent API calls within minimum interval
      const lastCallTime = lastApiCallTime.get(cacheKey);
      const now = Date.now();
      if (lastCallTime && (now - lastCallTime) < MIN_API_CALL_INTERVAL) {
        console.log(`⏱️ Rate limit: Last API call was ${Math.round((now - lastCallTime) / 1000)}s ago, skipping (min interval: ${MIN_API_CALL_INTERVAL / 1000}s)`);
        hasInitializedRef.current = true;
        // Try to get cached data again (in case it was just added)
        const rateLimitedCachedData = recommendationsCache.get(cacheKey);
        if (rateLimitedCachedData) {
          setRecommendations(rateLimitedCachedData);
          setAddedRecommendations(new Set());
          setCurrentIndex(0);
        }
        return; // Exit early - rate limited
      }
      
      // QUATERNARY GUARD: Prevent re-initialization if already initialized in this mount
      // This is a backup - the cache should catch most cases
      if (hasInitializedRef.current) {
        console.log('🔒 Already initialized in this mount cycle, skipping');
        return; // Exit early - already initialized
      }
      
      console.log('🔄 Modal opened - loading recommendations for', cacheKey);
      
      // Mark as loading to prevent duplicate calls
      loadingCache.add(cacheKey);
      hasInitializedRef.current = true;
      
      // Reset UI state
      setAddedRecommendations(new Set());
      setCurrentIndex(0);
      setLoadingRecommendations(true);
      
      // Capture current day and trip values at the moment modal opens
      const currentDay = day;
      const currentTrip = trip;
      const existingActivities = (currentDay.itinerary || []).map((item: any) => item.name || '').filter(Boolean);
      
      console.log('📡 Calling recommendations API...');
      
      // Record API call time BEFORE making the call
      lastApiCallTime.set(cacheKey, Date.now());
      
      // Make API call - this is the ONLY place the API is called
      TripPlannerAPI.generateRecommendations(
        currentTrip.tripId,
        currentDay.dayId,
        existingActivities
      )
        .then((response) => {
          if (response.success && response.recommendations) {
            const apiRecommendations = response.recommendations.map((rec: any) => ({
              ...rec,
              isLiked: false,
              isDisliked: false
            }));
            // Store in both state and cache
            setRecommendations(apiRecommendations);
            recommendationsCache.set(cacheKey, apiRecommendations);
            console.log('✅ Recommendations loaded and cached:', apiRecommendations.length);
          } else {
            throw new Error(response.message || 'Failed to generate recommendations');
          }
        })
        .catch((error) => {
          console.error('❌ Error loading recommendations:', error);
          setRecommendations([]);
          // Remove from cache on error so it can be retried
          clearCacheForKey(cacheKey);
          hasInitializedRef.current = false; // Allow retry
        })
        .finally(() => {
          setLoadingRecommendations(false);
          loadingCache.delete(cacheKey);
        });
    } else {
      // When modal closes, reset initialization flag
      // But don't reset recommendations/loading if still loading (API call in progress)
      const cacheKey = getCacheKey(trip.tripId, day.dayId);
      const isStillLoading = loadingCache.has(cacheKey);
      
      console.log('🔒 Modal closed', isStillLoading ? '(API call still in progress)' : '');
      hasInitializedRef.current = false;
      
      // Only reset recommendations and loading state if not loading
      // This preserves the loading state if API call is still in progress
      if (!isStillLoading) {
        setRecommendations([]);
        setLoadingRecommendations(false);
      }
      setAddedRecommendations(new Set());
      setCurrentIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]); // ONLY depend on isOpen and mode - day and trip are captured at open time

  // Measure container width
  useEffect(() => {
    if (!contentContainerRef.current) return;
    
    const updateWidth = () => {
      if (contentContainerRef.current) {
        setContainerWidth(contentContainerRef.current.clientWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen, mode]);

  // Smooth scroll handler
  const handleScroll = React.useCallback(() => {
    if (!scrollContainerRef.current || containerWidth === 0) return;
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      if (!scrollContainerRef.current || containerWidth === 0) return;
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const sideCardWidth = containerWidth * 0.50;
      const gap = 16;
      const padding = containerWidth * 0.125;
      const adjustedScrollLeft = scrollLeft + padding;
      const cardUnit = sideCardWidth + gap;
      const floatIdx = cardUnit > 0 ? (adjustedScrollLeft / cardUnit) : 0;
      setScrollIndex(floatIdx);
      const rounded = Math.round(floatIdx);
      if (rounded !== currentIndex) {
        setCurrentIndex(rounded);
      }
    });
  }, [currentIndex, containerWidth]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  const scrollToIndex = React.useCallback((index: number) => {
    if (!scrollContainerRef.current || containerWidth === 0) return;
    const container = scrollContainerRef.current;
    const sideCardWidth = containerWidth * 0.50;
    const gap = 16;
    const padding = containerWidth * 0.125;
    let cardPosition = 0;
    for (let i = 0; i < index; i++) {
      cardPosition += sideCardWidth + gap;
    }
    const scrollPosition = cardPosition - padding;
    container.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: 'smooth'
    });
  }, [containerWidth]);

  // Center first card when recommendations load
  useEffect(() => {
    if (recommendations.length > 0 && scrollContainerRef.current && containerWidth > 0 && mode === 'recommendations') {
      setTimeout(() => {
        scrollToIndex(0);
      }, 100);
    }
  }, [recommendations.length, containerWidth, scrollToIndex, mode]);

  const handleAddActivityFromRecommendation = async (recommendation: Recommendation) => {
    if (addedRecommendations.has(recommendation.id) || addingRecommendationId === recommendation.id) return;
    
    setAddingRecommendationId(recommendation.id);
    
    try {
      const itemData = {
        name: recommendation.name,
        placeName: recommendation.placeName,
        itemType: recommendation.type.toLowerCase(),
        description: recommendation.description,
        location: {
          lat: recommendation.location.lat,
          lon: recommendation.location.lon
        },
        estimatedCost: parseFloat((recommendation.estimatedCost / 100).toFixed(2)),
        startTime: "10:00",
        endTime: "12:00"
      };

      const response = await TripPlannerAPI.addItemToDay(
        trip.tripId,
        day.dayId,
        itemData
      );

      if (response.success) {
        if (onAddActivity) {
          onAddActivity(dayIndex, response.item);
        }
        setAddedRecommendations(prev => new Set(prev).add(recommendation.id));
      } else {
        throw new Error(response.message || 'Failed to add activity');
      }
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Failed to add activity. Please try again.');
    } finally {
      setAddingRecommendationId(null);
    }
  };

  const formatCost = (costInCents: number): string => {
    return `$${(costInCents / 100).toFixed(2)}`;
  };

  const getItemEmoji = (type: string) => {
    const emojiMap: Record<string, string> = {
      'restaurant': '🍽️',
      'attraction': '🏛️',
      'shopping': '🛍️',
      'activity': '🎯',
      'entertainment': '🎭',
      'transportation': '🚗',
      'lodging': '🏨',
      'museum': '🖼️',
    };
    return emojiMap[type.toLowerCase()] || '📍';
  };

  const handleSave = () => {
    if (!name || !placeName || !description || !startTime || !endTime || !itemType) {
      alert('Please fill in all required fields');
      return;
    }
    
    onSave({
      name,
      placeName,
      description,
      startTime,
      endTime,
      location,
      itemType,
      estimatedCost
    });
    
    // Reset form - state is managed in parent, so reset there
    setFormData({
      name: '',
      placeName: '',
      description: '',
      startTime: '',
      endTime: '',
      location: { lat: 0, lon: 0 },
      itemType: '',
      estimatedCost: 0,
      aiEditPrompt: '',
      mode: 'recommendations',
      isAIMode: true
    });
  };

  const handleAIEdit = async () => {
    if (!aiEditPrompt.trim() || !onAIEdit) {
      alert('Please enter a description of the changes you want to make');
      return;
    }
    
    setIsGeneratingAI(true);
    try {
      const editedItem = await onAIEdit(aiEditPrompt);
      
      if (editedItem) {
        setFormData(prev => ({
          ...prev,
          name: editedItem.name || '',
          placeName: editedItem.placeName || '',
          description: editedItem.description || '',
          startTime: editedItem.startTime || '',
          endTime: editedItem.endTime || '',
          itemType: editedItem.itemType || '',
          estimatedCost: editedItem.estimatedCost || 0,
          location: editedItem.location ? {
            lat: editedItem.location.lat || 0,
            lon: editedItem.location.lon || 0
          } : prev.location,
          aiEditPrompt: '',
          isAIMode: false
        }));
      } else {
        alert('Failed to get edited item data');
      }
    } catch (error) {
      console.error('Error editing with AI:', error);
      alert(`Failed to edit item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingAI(false);
    }
  };


  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      scrollPositionRef.current = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPositionRef.current);
      };
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [isOpen]);

  // Form initialization is handled in parent (TripViewPage) to persist across remounts
  // No initialization logic here - parent manages all state

  const handleClose = () => {
    setIsGeneratingAI(false);
    // Form data reset is handled in parent (TripViewPage) when modal closes
    onClose();
  };

  const handleAIApply = async () => {
    if (!aiEditPrompt.trim()) {
      alert('Please enter a description of the activity you want to create');
      return;
    }
    
    if (!onAIGeneratePreview) {
      alert('AI generation is not available. Please use Manual Mode.');
      return;
    }
    
    setIsGeneratingAI(true);
    try {
      const generatedItem = await onAIGeneratePreview(aiEditPrompt);
      
      if (generatedItem) {
        // Populate the form with the generated data
        setFormData(prev => ({
          ...prev,
          name: generatedItem.name || '',
          placeName: generatedItem.placeName || '',
          description: generatedItem.description || '',
          startTime: generatedItem.startTime || '',
          endTime: generatedItem.endTime || '',
          itemType: generatedItem.itemType || '',
          // Convert from cents to dollars
          estimatedCost: generatedItem.estimatedCost ? generatedItem.estimatedCost / 100 : 0,
          location: generatedItem.location ? {
            lat: generatedItem.location.lat || 0,
            lon: generatedItem.location.lon || 0
          } : prev.location,
          aiEditPrompt: '',
          mode: 'manual'
        }));
      } else {
        alert('Failed to get generated item data');
      }
    } catch (error) {
      console.error('Error generating with AI:', error);
      alert(`Failed to generate activity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  if (!isOpen) return null;

  const dayTitle = day?.title || `Day ${dayIndex + 1}`;

  return (
    <div 
      className="modal-overlay"
      onWheel={(e) => {
        const target = e.target as HTMLElement;
        const modalContent = target.closest('.modal-content');
        if (e.currentTarget === e.target || !modalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      style={{ overflow: 'hidden' }}
    >
      <div className="modal-content max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-[#E3E8E5] flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0B1412]">
            {isEditing ? `Edit ${editingItem.name}` : `Add new Activity for ${dayTitle}`}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F2FBF9] transition-colors"
            disabled={isSaving || isGeneratingAI || isDeleting}
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div ref={contentContainerRef} className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Mode Toggle - Only show when adding (not editing) */}
            {!isEditing && (
              <div className="p-1 bg-[#F2FBF9] rounded-xl border border-[#E3E8E5]">
                <div className="flex items-center gap-1 w-full">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, mode: 'recommendations' }))}
                    disabled={isSaving || isGeneratingAI || isDeleting}
                    className={`flex-1 py-2.5 px-3 rounded-lg font-medium text-xs transition-all whitespace-nowrap flex items-center justify-center ${
                      mode === 'recommendations'
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-transparent text-[#0B1412] hover:bg-white/50'
                    }`}
                  >
                    Suggestions
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, mode: 'ai' }))}
                    disabled={isSaving || isGeneratingAI || isDeleting}
                    className={`flex-1 py-2.5 px-3 rounded-lg font-medium text-xs transition-all whitespace-nowrap flex items-center justify-center ${
                      mode === 'ai'
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-transparent text-[#0B1412] hover:bg-white/50'
                    }`}
                  >
                    AI
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, mode: 'manual' }))}
                    disabled={isSaving || isGeneratingAI || isDeleting}
                    className={`flex-1 py-2.5 px-3 rounded-lg font-medium text-xs transition-all whitespace-nowrap flex items-center justify-center ${
                      mode === 'manual'
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-transparent text-[#0B1412] hover:bg-white/50'
                    }`}
                  >
                    Manual
                  </button>
                </div>
              </div>
            )}

            {/* Edit Mode Toggle - Only show when editing */}
            {isEditing && (
              <div className="flex items-center justify-between p-4 bg-[#F2FBF9] rounded-xl border border-[#E3E8E5]">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-[#0B1412]">AI Mode</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!isAIMode}
                      onChange={(e) => setFormData(prev => ({ ...prev, isAIMode: !e.target.checked }))}
                      className="sr-only peer"
                      disabled={isSaving || isGeneratingAI || isDeleting}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                  <span className="text-sm font-medium text-[#0B1412]">Manual Mode</span>
                </div>
              </div>
            )}

            {/* Recommendations Mode */}
            {!isEditing && mode === 'recommendations' && (
              <div>
                {loadingRecommendations ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="loading-spinner w-8 h-8 mb-4"></div>
                    <span className="text-[#6F7974] text-sm">Loading recommendations...</span>
                  </div>
                ) : recommendations.length > 0 ? (
                  <>
                    <div 
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                      className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory"
                      style={{
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        paddingLeft: containerWidth > 0 ? `${containerWidth * 0.125}px` : '12.5%',
                        paddingRight: containerWidth > 0 ? `${containerWidth * 0.125}px` : '12.5%'
                      }}
                    >
                      {recommendations.map((recommendation, index) => {
                        const roundedIndex = Math.round(scrollIndex);
                        const isMain = index === roundedIndex;
                        const isAdded = addedRecommendations.has(recommendation.id);
                        const distance = Math.abs(scrollIndex - index);
                        const clamped = Math.min(distance, 1);
                        const scale = 0.9 + (1 - clamped) * 0.15;
                        const opacity = 0.6 + (1 - clamped) * 0.4;
                        const shadow = isMain ? '0 12px 28px rgba(0,0,0,0.15)' : '0 8px 16px rgba(0,0,0,0.08)';
                        const baseWidth = containerWidth > 0 
                          ? (isMain ? containerWidth * 0.75 : containerWidth * 0.50)
                          : (isMain ? '75%' : '50%');
                        
                        return (
                          <div
                            key={recommendation.id}
                            className={`flex-shrink-0 bg-white rounded-2xl border border-[#E3E8E5] shadow-lg transition-all duration-300 snap-center flex flex-col ${
                              isMain ? 'p-6' : 'p-4'
                            }`}
                            style={{ 
                              width: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                              minWidth: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                              maxWidth: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                              transform: `scale(${scale.toFixed(3)})`,
                              opacity: opacity,
                              boxShadow: shadow,
                              transition: 'transform 0.12s ease-out, opacity 0.12s ease-out, box-shadow 0.2s ease',
                              scrollSnapAlign: 'center',
                              scrollSnapStop: 'always'
                            }}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <h3 
                                className="text-[#0B1412] font-bold flex-1"
                                style={{ fontSize: isMain ? '20px' : '18px', fontWeight: 700 }}
                              >
                                {recommendation.name}
                              </h3>
                              <div className={`flex items-center justify-center flex-shrink-0 ml-2 ${
                                isMain ? 'w-8 h-8 text-xl' : 'w-7 h-7 text-lg'
                              }`}>
                                {getItemEmoji(recommendation.type)}
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <span 
                                className="inline-flex items-center px-3 py-1.5 rounded-2xl border border-[#E3E8E5] text-[#0B1412]"
                                style={{ fontSize: '13px', fontWeight: 600, height: '32px' }}
                              >
                                {formatCost(recommendation.estimatedCost)}
                              </span>
                            </div>

                            {recommendation.description && (
                              <p 
                                className={`text-[#6F7974] leading-relaxed mb-4 line-clamp-3`}
                                style={{ 
                                  fontSize: isMain ? '15px' : '14px',
                                  lineHeight: '1.5'
                                }}
                              >
                                {recommendation.description}
                              </p>
                            )}

                            {recommendation.placeName && (
                              <p className="text-[#6F7974] text-sm mb-4">
                                📍 {recommendation.placeName}
                              </p>
                            )}

                            <button
                              onClick={() => handleAddActivityFromRecommendation(recommendation)}
                              disabled={isAdded || addingRecommendationId === recommendation.id}
                              className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 mt-auto ${
                                isAdded
                                  ? 'bg-[#E3E8E5] text-[#6F7974] cursor-not-allowed'
                                  : addingRecommendationId === recommendation.id
                                  ? 'bg-primary-300 text-white cursor-not-allowed'
                                  : 'bg-primary-500 text-white hover:bg-primary-600 shadow-md hover:shadow-lg'
                              }`}
                            >
                              {isAdded ? (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Added
                                </>
                              ) : addingRecommendationId === recommendation.id ? (
                                <>
                                  <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  Add Activity
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {recommendations.length > 1 && (
                      <div className="flex justify-center gap-2 mt-4">
                        {recommendations.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => scrollToIndex(index)}
                            className={`transition-all duration-200 rounded-full ${
                              index === currentIndex
                                ? 'w-2.5 h-2.5 bg-primary-500'
                                : 'w-2 h-2 bg-[#E3E8E5] hover:bg-primary-300'
                            }`}
                            aria-label={`Go to recommendation ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#E3E8E5] rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">💡</span>
                    </div>
                    <p className="text-lg font-semibold text-[#0B1412] mb-2">No recommendations available</p>
                    <p className="text-sm text-[#6F7974]">Try adding more details to your day summary</p>
                  </div>
                )}
              </div>
            )}

            {/* AI Mode - Only for adding new items */}
            {!isEditing && mode === 'ai' && (
              <div className="input-group">
                <label className="input-label">
                  Describe the activity you want to create
                </label>
                <textarea
                  value={aiEditPrompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, aiEditPrompt: e.target.value }))}
                  placeholder="e.g., Create a morning visit to a historic temple, followed by a traditional lunch at a local restaurant..."
                  className="input-field resize-none"
                  rows={4}
                  disabled={isSaving || isGeneratingAI || isDeleting}
                />
              </div>
            )}

            {/* Edit Mode AI Content */}
            {isEditing && isAIMode && (
              <div className="input-group">
                <label className="input-label">
                  What changes would you like to make to this activity?
                </label>
                <textarea
                  value={aiEditPrompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, aiEditPrompt: e.target.value }))}
                  placeholder="e.g., Change the time to 2 PM, update the description to include family-friendly activities, or modify the cost..."
                  className="input-field resize-none"
                  rows={4}
                  disabled={isSaving || isGeneratingAI || isDeleting}
                />
              </div>
            )}

            {/* Manual Mode Content */}
            {((!isEditing && mode === 'manual') || (isEditing && !isAIMode)) && (
              <>
                <div className="input-group">
                  <label className="input-label">
                    Activity Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Visit Senso-ji Temple"
                    className="input-field w-full"
                    disabled={isSaving || isGeneratingAI}
                    required
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">
                    Place Name *
                  </label>
                  <input
                    type="text"
                    value={placeName}
                    onChange={(e) => setFormData(prev => ({ ...prev, placeName: e.target.value }))}
                    placeholder="e.g., Senso-ji Temple"
                    className="input-field"
                    disabled={isSaving || isGeneratingAI}
                    required
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">
                    Description *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the activity..."
                    className="input-field resize-none"
                    rows={3}
                    disabled={isSaving || isGeneratingAI}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <label className="input-label">
                      Start Time *
                    </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                    className="input-field"
                    disabled={isSaving || isGeneratingAI}
                    required
                  />
                  </div>
                  <div className="input-group">
                    <label className="input-label">
                      End Time *
                    </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                    className="input-field"
                    disabled={isSaving || isGeneratingAI}
                    required
                  />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <label className="input-label">
                      Item Type *
                    </label>
                    <select
                      value={itemType}
                      onChange={(e) => setFormData(prev => ({ ...prev, itemType: e.target.value }))}
                      className="input-field"
                      disabled={isSaving || isGeneratingAI}
                      required
                    >
                      <option value="">Select type</option>
                      <option value="activity">Activity</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="attraction">Attraction</option>
                      <option value="transportation">Transportation</option>
                      <option value="lodging">Lodging</option>
                      <option value="shopping">Shopping</option>
                      <option value="entertainment">Entertainment</option>
                    </select>
                  </div>
                  <div className="input-group">
                    <label className="input-label">
                      Estimated Cost (USD)
                    </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={estimatedCost}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedCost: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="input-field"
                    disabled={isSaving || isGeneratingAI}
                  />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[#E3E8E5] flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isSaving || isGeneratingAI || isDeleting}
            className="btn-secondary"
          >
            Cancel
          </button>
          {!isEditing && mode === 'ai' ? (
            <button
              onClick={handleAIApply}
              disabled={isSaving || isGeneratingAI || isDeleting || !aiEditPrompt.trim()}
              className="btn-primary"
            >
              {isGeneratingAI ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Applying Changes...
                </>
              ) : (
                'Apply Changes'
              )}
            </button>
          ) : isEditing && isAIMode ? (
            <button
              onClick={handleAIEdit}
              disabled={isSaving || isGeneratingAI || isDeleting || !aiEditPrompt.trim()}
              className="btn-primary"
            >
              {isGeneratingAI ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Applying Changes...
                </>
              ) : (
                'Apply Changes'
              )}
            </button>
          ) : mode === 'recommendations' ? (
            // No save button in recommendations mode - activities are added directly
            null
          ) : (
            <button
              onClick={handleSave}
              disabled={
                isSaving || 
                isGeneratingAI || 
                isDeleting || 
                (!name.trim() || !description.trim() || !startTime || !endTime || !itemType)
              }
              className="btn-primary"
            >
              {isSaving ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Saving...
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Save'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddItineraryItemModal;
